/* Modale de création de session d'examen (bloc 08-A) — partagée entre la liste
   « Examens sur table » et la page de structuration post-import (spec 11). */

import { useEffect, useState } from 'react';
import { X } from '@phosphor-icons/react';
import { api } from '../lib/api';
import { useExamImportJobs } from '../hooks/use-exam-import-jobs';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';

interface ExamDoc {
  id: string;
  kind: string;
  title: string;
  archived_at: string | null;
}
interface Classroom {
  id: string;
  name: string;
  active_student_count: number;
}

export default function CreateExamSessionModal({
  onClose,
  onCreated,
  initialDocumentId,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
  initialDocumentId?: string;
}) {
  const [docs, setDocs] = useState<ExamDoc[]>([]);
  const [documentId, setDocumentId] = useState(initialDocumentId ?? '');
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [classroomId, setClassroomId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { notifySessionCreated } = useExamImportJobs();

  useEffect(() => {
    api.content.list
      .query({ kind: 'exam', includeArchived: false, limit: 100 })
      .then((r) => setDocs((r as { items: ExamDoc[] }).items.filter((d) => !d.archived_at)))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    setClassroomId('');
    if (!documentId) { setClassrooms([]); return; }
    api.assignments.assignableClassrooms
      .query({ documentId })
      .then((r) => setClassrooms(r as Classroom[]))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [documentId]);

  const create = async () => {
    if (!documentId || !classroomId) return;
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.exam.createSession.mutate({
        examDocumentId: documentId,
        classroomId,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });
      notifySessionCreated();
      onCreated(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/45 flex items-end justify-center z-50 pt-[env(safe-area-inset-top)] min-[620px]:items-center">
      <div
        className="bg-card w-full max-w-[520px] max-h-[92dvh] overflow-y-auto rounded-t-[20px] pt-5 px-[18px] pb-[calc(20px+env(safe-area-inset-bottom))] flex flex-col gap-[14px] min-[620px]:rounded-[20px]"
        role="dialog"
        aria-modal="true"
        aria-label="Nouvelle session d'examen"
      >
        <div className="flex items-center justify-between">
          <h2 className="m-0 text-[18px] font-extrabold">{`Nouvelle session d'examen`}</h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Fermer">
            <X size={18} weight="regular" />
          </Button>
        </div>

        {error && <Alert>{error}</Alert>}

        <label className="flex flex-col gap-[6px] text-[13px] font-bold text-muted-foreground">
          {`Sujet d'examen`}
          <NativeSelect value={documentId} disabled={Boolean(initialDocumentId)} onChange={(e) => setDocumentId(e.target.value)}>
            <option value="">{`— choisir un document Évaluation —`}</option>
            {docs.map((d) => (
              <option key={d.id} value={d.id}>{d.title}</option>
            ))}
          </NativeSelect>
        </label>

        {docs.length === 0 && !error && (
          <p className="text-[13px] text-muted-foreground">{`Aucun document de type « Évaluation ». Créez-en un dans la bibliothèque.`}</p>
        )}

        <label className="flex flex-col gap-[6px] text-[13px] font-bold text-muted-foreground">
          Classe
          <NativeSelect value={classroomId} disabled={!documentId} onChange={(e) => setClassroomId(e.target.value)}>
            <option value="">— choisir une classe —</option>
            {classrooms.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.active_student_count} élèves)</option>
            ))}
          </NativeSelect>
        </label>

        <label className="flex flex-col gap-[6px] text-[13px] font-bold text-muted-foreground">
          Date (optionnel)
          <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
        </label>

        <div className="flex gap-[10px] justify-end flex-wrap">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button disabled={busy || !documentId || !classroomId} onClick={() => void create()}>
            {busy ? 'Création…' : 'Créer'}
          </Button>
        </div>
      </div>
    </div>
  );
}
